/* @flow */
import _ from 'lodash';
import React, {PropTypes} from 'react';
import { connect } from 'react-redux';
import {
  setClearingNumberSuppressError,
  setAccountNumberSuppressError,
  syncBankInfoFromUser,
  reset
} from 'redux/modules/bank-info';
import Translate from 'react-translate-component';
import { proxyCounterpart } from '../../../scripts/proxyCounterpart';
import ItpVacationCheckBoxes from './check-boxes/itp_vacation';
import NotificationCheckBoxes from './check-boxes/notification';
import { GeneralError, SpinnerButton, SelectBox } from 'components';
import { UserService, Utils } from 'frilans-finans';
import BankInfoForm from './bank-info-form';

class BankInfo extends React.Component {

  static contextTypes = {
    user: PropTypes.object.isRequired,
    taxkeys: PropTypes.array.isRequired
  };

  static propTypes = {
    user: PropTypes.object.isRequired,
    bankInfo: PropTypes.object.isRequired,
    isValid: PropTypes.bool.isRequired,
    reset: PropTypes.func.isRequired,
    setClearingNumberSuppressError: PropTypes.func.isRequired,
    setAccountNumberSuppressError: PropTypes.func.isRequired,
    syncBankInfoFromUser: PropTypes.func.isRequired,
    taxkeys: PropTypes.array.isRequired
  };

  constructor (props, context) {
    super(props, context);

    this.state = {
      posting: false,
      postError: false,
      errorMessage: '',
      user: this.getUserObject(context.user),
      taxkeyOptions: [{
        disabled: true,
        value: '',
        key: proxyCounterpart('CALC_KEY')}].concat(this.prepareTaxKeys(props.taxkeys)),
      selectedTaxkey: context.user.defaultTaxkeyId,
      showErrorWithoutBlur: false
    };

    this.callback = this.callback.bind(this);
    this.clickedSubmit = this.clickedSubmit.bind(this);
    this.disableITP = this.disableITP.bind(this);
    this.getBankAccountFields = this.getBankAccountFields.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  getUserObject (user) {
    let userObj = Utils.getInputValueObjects(user);

    return {
      id: userObj.id,
      professionTitle: userObj.professionTitle,
      professionID: userObj.professionId,
      displayHsd: userObj.displayHsd,
      canChangeItp: userObj.canChangeItp,
      saveITP: userObj.saveITP,
      itpRate: userObj.itpRate,
      saveVacationPay: userObj.saveVacationPay,
      defaultTaxkeyId: userObj.defaultTaxkeyId,
      receiveSmsNotifications: userObj.receiveSmsNotifications,
      receiveEmailNotifications: userObj.receiveEmailNotifications,
      imageSrc: userObj.imageSrc,
      socialSecurityNr: userObj.socialSecurityNr
    };
  }

  prepareTaxKeys (taxkeys) {
    let taxKeyArray = [];

    for (let taxkey of taxkeys) {
      let taxKeyName = taxkey.taxkeyName;

      if (taxkey.payrollTax === 19.73) {
        taxKeyName = `${taxkey.taxAmount}%, 19-23 ${proxyCounterpart('YEARS')}`;
      }
      taxKeyArray.push({
        key: taxKeyName,
        value: taxkey.id
      });
    }

    return taxKeyArray;
  }

  callback (name, values) {
    var tempFormValues = this.state.user;

    Object.assign(tempFormValues[name], values);
    this.setState({user: tempFormValues});
  }

  componentWillUpdate (nextProps, nextState) {
    nextState.taxkeyOptions = [{
      disabled: true,
      value: '',
      key: proxyCounterpart('CALC_KEY')}].concat(this.prepareTaxKeys(nextProps.taxkeys));
  }

  componentWillUnmount () {
    this.props.reset();
  }

  componentWillReceiveProps (nextProps) {
    // We need to wait till banks are loaded, because it has to identify the
    // bank by selecting it from the list by the clearing number in redux.
    if (!_.isEqual(this.props.bankInfo.banks, nextProps.bankInfo.banks)) {
      this.props.syncBankInfoFromUser();
    }
  }

  disableITP () {
    return !this.state.user.canChangeItp.value && !this.state.user.saveITP.value;
  }

  handleSuccess () {
    this.setState({
      posting: false,
      userUpdated: true,
      postError: false
    });
  }

  handleError (error) {
    let errors = UserService.getError(error);

    this.setState({
      posting: false,
      userUpdated: false,
      postError: true,
      errorMessage: errors
    });
  }

  getBankAccountFields () {
    if (this.props.bankInfo.selectedBank === 'IBAN') {
      return {
        internationalAccount: 'on',
        bic: this.props.bankInfo.clearingNumber.value,
        iban: this.props.bankInfo.accountNumber.value
      };
    }

    return {
      internationalAccount: 'off',
      accountClearingNr: this.props.bankInfo.clearingNumber.value,
      accountNr: this.props.bankInfo.accountNumber.value
    };
  }

  stripBankFields (user) {
    let cleanUser = {...user};
    delete cleanUser.internationalAccount;
    delete cleanUser.accountClearingNr;
    delete cleanUser.accountNr;
    return cleanUser;
  }

  clickedSubmit (event) {
    event.preventDefault();
    this.props.setClearingNumberSuppressError(false);
    this.props.setAccountNumberSuppressError(false);

    if (Utils.validateInputValues(this.stripBankFields(this.state.user))) {
      if (this.props.isValid) {
        this.submitForm();
      }
    } else {
      this.setState({showErrorWithoutBlur: true});
    }
  }

  submitForm () {
    var bankParams = this.getBankAccountFields();
    var params = Utils.getPostParams(this.state.user);
    Object.assign(params, bankParams);
    delete params.canChangeItp;
    params.defaultTaxkeyId = parseInt(params.defaultTaxkeyId);

    UserService.update(params)
      .then(this.handleSuccess)
      .catch(this.handleError);

    this.setState({
      posting: true,
      userUpdated: false,
      postError: false
    });
  }

  renderPostSuccess () {
    return (
      <p className='info'><Translate content='SETTINGS_UPDATED' /></p>
    );
  }

  renderPostError () {
    if (!this.state.errorMessage) {
      return (<GeneralError />);
    } else {
      return (
        <div>
        {this.state.errorMessage.map((error, i) => {
          return (
            <p className="error" key={i}>{error}</p>
          );
        })}
        </div>
      );
    }
  }

  render () {
    return (
      <div className='bank-info'>
        <h2><Translate content='SALARY_SETTINGS_TITLE'/></h2>

        <div className="section">
          <h3><Translate content='BANK_INFO_TITLE'/></h3>
          <BankInfoForm />
        </div>
        <div className="section">
          <h3><Translate content='INCOME_TAX'/></h3>
          <SelectBox name='defaultTaxkeyId' options={this.state.taxkeyOptions}
            callback={this.callback} errorMessage={proxyCounterpart('CALC_KEY_ERROR')}
            required='true' value={this.state.selectedTaxkey}
            showErrorWithoutBlur={this.state.showErrorWithoutBlur} />
        </div>

        <ItpVacationCheckBoxes callback={this.callback}
          disableITP={this.disableITP()}
          values={{
            canChangeItp: this.state.user.canChangeItp,
            saveITP: this.state.user.saveITP,
            itpRate: this.state.user.itpRate,
            saveVacationPay: this.state.user.saveVacationPay
          }}
          showErrorWithoutBlur={this.state.showErrorWithoutBlur}
        />

        <NotificationCheckBoxes callback={this.callback}
          values={{
            receiveSmsNotifications: this.state.user.receiveSmsNotifications,
            receiveEmailNotifications: this.state.user.receiveEmailNotifications
          }}
        />

        {this.state.userUpdated &&
          this.renderPostSuccess()
        }

        {this.state.postError &&
          this.renderPostError()
        }

        <div className='button-wrapper'>
          <SpinnerButton
            text={proxyCounterpart('SAVE_SETTINGS')}
            loading={this.state.posting}
            classes='btn'
            type='button'
            callback={this.clickedSubmit}/>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({bankInfo, user, taxkeys}) => ({
  bankInfo,
  user,
  isValid: bankInfo.clearingNumber.isValid && bankInfo.accountNumber.isValid,
  taxkeys
});

const mapDispatchToProps = {
  setClearingNumberSuppressError,
  setAccountNumberSuppressError,
  reset,
  syncBankInfoFromUser
};

export default connect(mapStateToProps, mapDispatchToProps)(BankInfo);



/** WEBPACK FOOTER **
 ** ./src/views/settings/salary-info/salary-info.js
 **/